/**
 * Web override for `zen-core-elements/src/shared/constants`.
 *
 * The inconsistent font sizes across web screens have a concrete cause: the
 * project carries two independent typographic scales.
 *
 *   src/themes/appTheme.tsx               keys off `isLandscape`
 *     headerFontSize 20 | mediumFontSize 17 | smallerFontSize 16 | error 13
 *
 *   zen-core-elements/src/shared/constants.ts  keys off Dimensions.width,
 *   read once at module load
 *     width > 1024 -> headerFontSize 22 | mediumFontSize 19 | smaller 18 |
 *     error 15
 *
 * A screen assembled from app components and a screen assembled from Zen
 * components therefore render body text two points apart in the same browser
 * window, and the gap widens as the window does. On a desktop browser - always
 * past the 1024 breakpoint - the Zen half of the UI is permanently at its
 * largest step while the app half is not.
 *
 * This module republishes the library's constants with the *desktop* scale
 * substituted - the same webFontScale that appTheme.tsx applies to the app
 * half of the UI on web - and webpack aliases the library path to it for the
 * web build only. Android and tablet keep the original values, because Metro
 * never sees this file.
 *
 * Colours, fonts and every other export pass through untouched - only the size
 * scale is unified.
 */

export * from 'zen-core-elements/src/shared/constants';

/**
 * The desktop scale from appTheme.tsx (webFontScale).
 * Keys and semantics are identical to the library's own `fontSize` object, so
 * every existing consumer keeps working unchanged.
 */
export const fontSize = {
  headerFontSize: 20, // page header
  largerFontSize: 16, // section title, button
  mediumFontSize: 15, // body text
  smallerFontSize: 14, // label, input
  smallFontSize: 13, // search placeholder, helper
  smallHeaderFontSize: 12,
  tinyFontSize: 13,
  errorFontSize: 12, // error, tooltip
  badgeFontSize: 10,
};

/**
 * The library computes this from Dimensions at module load (16 above 600px,
 * 14 below). This follows webFontScale.iconSize so an icon never disagrees
 * with the text beside it.
 */
export const iconSize = 18;
